import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Car, useCar, CarContext } from '@/contexts/CarContext';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthProvider';

const SELECTED_CAR_KEY = 'selectedCarId';
const MAIN_CAR_KEY = 'mainCarId';

interface CarSelectionContextType {
  currentCarId: string | null;
  currentCar: Car | null;
  selectedCarId: string | null;
  mainCarId: string | null;
  selectCar: (carId: string) => void;
  resetToMainCar: () => void;
  setMainCar: (carId: string) => void;
}

const CarSelectionContext = createContext<CarSelectionContextType | undefined>(undefined);

export const CarSelectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Safely get car context - use useContext directly to avoid throwing during hot reload
  const carContext = useContext(CarContext);
  const cars = carContext?.cars || [];
  const mainCarIdFromServer = carContext?.mainCarId || null;
  const { user } = useAuth();
  
  // Warn if CarProvider is not available (shouldn't happen in normal flow)
  if (!carContext) {
    console.warn('CarSelectionProvider: CarProvider context not available. Make sure CarSelectionProvider is wrapped in CarProvider.');
  }
  const autoSelectionDone = useRef(false);
  
  // Initialize selected car from localStorage
  const [selectedCarId, setSelectedCarId] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem(SELECTED_CAR_KEY);
      return stored || null;
    } catch (error) {
      console.warn('Failed to read selectedCarId from localStorage:', error);
      return null;
    }
  });

  // Initialize main car from localStorage or server
  const [mainCarId, setMainCarId] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem(MAIN_CAR_KEY);
      return stored || mainCarIdFromServer || null;
    } catch (error) {
      console.warn('Failed to read mainCarId from localStorage:', error);
      return mainCarIdFromServer || null;
    }
  });

  // Current car is either selected car or main car
  const currentCarId = selectedCarId ?? mainCarId;
  
  // Find current car with fallback logic for deleted cars
  const currentCar = useMemo(() => {
    if (!currentCarId) return null;
    
    const car = cars.find(car => car.id === currentCarId);
    if (car) return car;
    
    // If current car not found, try main car
    if (mainCarId && mainCarId !== currentCarId) {
      const mainCar = cars.find(car => car.id === mainCarId);
      if (mainCar) return mainCar;
    }
    
    // Fallback to first available car
    return cars[0] || null;
  }, [currentCarId, mainCarId, cars]);

  // Update main car when server data changes (e.g., on login)
  useEffect(() => {
    if (mainCarIdFromServer && mainCarIdFromServer !== localStorage.getItem(MAIN_CAR_KEY)) {
      try {
        localStorage.setItem(MAIN_CAR_KEY, mainCarIdFromServer);
        setMainCarId(mainCarIdFromServer);
        // Reset auto-selection flag when main car changes (e.g., on login)
        autoSelectionDone.current = false;
      } catch (error) {
        console.warn('Failed to save mainCarId to localStorage:', error);
      }
    }
  }, [mainCarIdFromServer]);

  // Handle case where selected car is deleted
  useEffect(() => {
    if (selectedCarId && !cars.find(car => car.id === selectedCarId)) {
      console.log('Selected car no longer exists, resetting to main car');
      resetToMainCar();
    }
    
    // Reset auto-selection flag when cars change significantly
    if (cars.length > 0 && !autoSelectionDone.current) {
      autoSelectionDone.current = false;
    }
  }, [cars, selectedCarId]);

  const selectCar = useCallback((carId: string) => {
    // Validate that the car exists
    if (!cars.find(car => car.id === carId)) {
      console.warn('Attempted to select non-existent car:', carId);
      return;
    }

    try {
      localStorage.setItem(SELECTED_CAR_KEY, carId);
      setSelectedCarId(carId);
      console.log('Car selected:', carId);
    } catch (error) {
      console.warn('Failed to save selectedCarId to localStorage:', error);
    }
  }, [cars]);

  const resetToMainCar = useCallback(() => {
    try {
      if (mainCarId) {
        localStorage.setItem(SELECTED_CAR_KEY, mainCarId);
        setSelectedCarId(mainCarId); // Set to main car explicitly
        console.log('Reset to main car:', mainCarId);
      } else {
        localStorage.removeItem(SELECTED_CAR_KEY);
        setSelectedCarId(null);
        console.log('Reset to main car (no main car available)');
      }
    } catch (error) {
      console.warn('Failed to reset to main car:', error);
    }
  }, [mainCarId]);

  const setMainCar = useCallback((carId: string) => {
    // Validate that the car exists
    if (!cars.find(car => car.id === carId)) {
      console.warn('Attempted to set non-existent car as main:', carId);
      return;
    }

    try {
      localStorage.setItem(MAIN_CAR_KEY, carId);
      setMainCarId(carId);
      console.log('Main car set:', carId);
    } catch (error) {
      console.warn('Failed to save mainCarId to localStorage:', error);
    }

    // Persist to server (cross-device/session)
    if (user?.id) {
      void (async () => {
        const { error } = await supabase
          .from('users')
          .update({ main_car_id: carId })
          .eq('id', user.id);
        if (error) {
          console.warn('Failed to persist main car to server:', error);
        } else {
          // Keep CarContext in sync immediately if available
          carContext?.setMainCar(carId);
        }
      })();
    }
  }, [cars]);

  // Auto-select main car if no car is selected and cars are available
  useEffect(() => {
    // Prevent infinite loops by tracking if auto-selection has been done
    if (autoSelectionDone.current) {
      console.log('⏭️ Auto-selection already done, skipping');
      return;
    }

    console.log('🔄 Auto-selection check:', {
      carsLength: cars.length,
      selectedCarId,
      mainCarId,
      cars: cars.map(c => ({ id: c.id, nickname: c.nickname }))
    });
    
    if (cars.length > 0 && !selectedCarId) {
      autoSelectionDone.current = true;
      
      if (mainCarId) {
        console.log('✅ No car selected, auto-selecting main car:', mainCarId);
        selectCar(mainCarId);
      } else if (cars.length === 1) {
        // If there's only one car and no main car set, make it the main car and select it
        console.log('✅ Only one car available, setting as main and selecting:', cars[0].id);
        setMainCar(cars[0].id);
        selectCar(cars[0].id);
      } else {
        // If there are multiple cars but no main car set, set the first one as main and select it
        console.log('✅ Multiple cars but no main car, setting first car as main and selecting:', cars[0].id);
        setMainCar(cars[0].id);
        selectCar(cars[0].id);
      }
    } else {
      console.log('⏭️ Auto-selection conditions not met');
    }
  }, [cars, selectedCarId, mainCarId]); // Removed selectCar and setMainCar from dependencies

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    currentCarId,
    currentCar,
    selectedCarId,
    mainCarId,
    selectCar,
    resetToMainCar,
    setMainCar,
  }), [
    currentCarId,
    currentCar,
    selectedCarId,
    mainCarId,
    selectCar,
    resetToMainCar,
    setMainCar,
  ]);

  return (
    <CarSelectionContext.Provider value={contextValue}>
      {children}
    </CarSelectionContext.Provider>
  );
};

export const useCarSelectionContext = () => {
  const context = useContext(CarSelectionContext);
  if (!context) {
    throw new Error('useCarSelectionContext must be used within CarSelectionProvider');
  }
  return context;
}; 